import { gql } from '@apollo/client';
import _ from 'lodash';
import type Quill from 'quill/core';
import { Delta } from 'quill/core';
import getApolloClient from '../../app/apollo/getApolloClient';
import getReduxStore from '../../app/redux/getReduxStore';
import type {
  MarkdownContentQuery,
  MarkdownContentQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import UIActions from '../../ui/UIActions';
import TableEmbed from '../formats/TableEmbed';
import generateDeltaRandomId from './generateDeltaRandomId';
import type { TableDocumentCell } from '../table/types';
import { SelectionType } from '../table/useTableSelection';
import { stringifyCellIdentity } from '../table/utils';
import type { Range } from '../types';

const generateTableDelta = (table: TableEmbed): Delta | null => {
  const selection = table.getSelection();
  switch (selection?.type) {
    case SelectionType.Cell: {
      const quill = table.getCellQuill(selection.identity);
      const range = table.getCellLastRange(selection.identity);
      if (quill && range?.length) {
        return quill.getContents(range.index, range.length);
      }

      return new Delta();
    }
    case SelectionType.Table:
    case SelectionType.Row:
    case SelectionType.Column: {
      const deltas = table.mapCellsInRange(selection, (content) => content);
      const rowCount = deltas.length;
      const columnCount = deltas[0]?.length ?? 0;
      if (rowCount <= 0 || columnCount <= 0) return null;
      const cells: { [identity: string]: TableDocumentCell } = {};
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
          const cell = deltas[rowIndex]?.[columnIndex];
          if (cell) {
            cells[stringifyCellIdentity(rowIndex, columnIndex)] = {
              content: cell,
            };
          }
        }
      }
      return new Delta().insert({
        'table-embed': {
          cells,
          rows: _.range(rowCount).map(() => ({
            insert: { id: generateDeltaRandomId() },
          })),
          columns: _.range(columnCount).map(() => ({
            insert: { id: generateDeltaRandomId() },
          })),
        },
      });
    }
    default:
      return null;
  }
};

export const getDeltaForMarkdown = (quill: Quill, range: Range) => {
  if (range.length === 1) {
    const [leaf] = quill.getLeaf(range.index);
    if (leaf instanceof TableEmbed) {
      const tableDelta = generateTableDelta(leaf);
      if (tableDelta) return tableDelta;
    }
  }

  const contents = quill.getContents(range.index, range.length);
  const [lastLine, lastLineOffset] = quill.getLine(range.index + range.length);
  if (!lastLine || lastLineOffset === 0) return contents;
  const lastLineFormats = lastLine.formats();

  return _.isEmpty(lastLineFormats)
    ? contents
    : contents.insert('\n', lastLineFormats);
};

const MARKDOWN_CONTENT_QUERY = gql`
  query markdownContent($delta: String!) {
    markdown: convert(delta: $delta)
  }
`;

const getMarkdownContent = async (
  quill: Quill,
  range: Range,
): Promise<string | null> => {
  const delta = getDeltaForMarkdown(quill, range);
  const client = await getApolloClient();
  const { data: { markdown } = {} } = await client.query<
    MarkdownContentQuery,
    MarkdownContentQueryVariables
  >({
    query: MARKDOWN_CONTENT_QUERY,
    variables: { delta: JSON.stringify(delta.ops) },
  });
  return markdown || null;
};

const copyAsMarkdown = async (quill: Quill, range: Range) => {
  const markdown = await getMarkdownContent(quill, range);
  if (!markdown) return;
  const store = getReduxStore();
  try {
    await navigator.clipboard.writeText(markdown.trim());
    store.dispatch(
      UIActions.pushToast({
        level: 'success',
        message: 'Markdown copied.',
      }),
    );
  } catch (error) {
    if (error.message === 'Document is not focused.') {
      store.dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Unable to copy markdown. Please keep the page focused while processing and try again.',
        }),
      );
    }
  }
};

export default copyAsMarkdown;
